import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableContainer,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../store';
import {
  fetchOrgStaff,
  resetAccessControlActionState,
  fetchOrgRoles,
  updateOrgStaffStatus
} from '../../../modules/accessControl/actions';
import {
  selectOrgStaff,
  selectOrgStaffLoading,
  selectCreateOrgStaffSuccess,
  selectCreateOrgStaffError,
  selectDeleteOrgStaffSuccess,
  selectDeleteOrgStaffError,
  selectUpdateOrgStaffStatusSuccess,
  selectUpdateOrgStaffStatusError,
  selectAccessControlActionMessage,
  selectUpdateOrgStaffRoleSuccess,
  selectUpdateOrgStaffRoleError
} from '../../../modules/accessControl/selectors';
import { OrgStaff } from '../../../modules/accessControl/types';
import {
  MainContainer,
  HeaderContainer,
  PageTitle,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell
} from '../../SuperAdmin/styles';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import { useSidebar } from '../../../context/SidebarContext';
import AlertPopup from '../../../components/common/AlertPopup';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import ActionPopup from '../../../components/common/ActionPopup';
import StaffModal from './components/StaffModal';
import UpdateStaffRoleModal from './components/UpdateStaffRoleModal';
import StaffDetailDrawer from './components/StaffDetailDrawer';

const StaffManagement: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();

  // Redux State
  const staff = useSelector(selectOrgStaff);
  const isLoading = useSelector(selectOrgStaffLoading);
  const createSuccess = useSelector(selectCreateOrgStaffSuccess);
  const deleteSuccess = useSelector(selectDeleteOrgStaffSuccess);
  const updateStatusSuccess = useSelector(selectUpdateOrgStaffStatusSuccess);
  const updateRoleSuccess = useSelector(selectUpdateOrgStaffRoleSuccess);
  const createError = useSelector(selectCreateOrgStaffError);
  const deleteError = useSelector(selectDeleteOrgStaffError);
  const updateStatusError = useSelector(selectUpdateOrgStaffStatusError);
  const updateRoleError = useSelector(selectUpdateOrgStaffRoleError);
  const actionMessage = useSelector(selectAccessControlActionMessage);

  const actionSuccess = createSuccess || deleteSuccess || updateStatusSuccess || updateRoleSuccess;
  const actionError = createError || deleteError || updateStatusError || updateRoleError;

  // Local State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState<OrgStaff | null>(null);
  const [alert, setAlert] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuUser, setMenuUser] = useState<OrgStaff | null>(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [detailUserMapping, setDetailUserMapping] = useState<OrgStaff | null>(null);

  // For activate (no remark needed)
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    userId: number;
  }>({ open: false, userId: 0 });

  // For deactivate/suspend (remark required) — uses ActionPopup
  const [actionPopup, setActionPopup] = useState<{
    open: boolean;
    userId: number;
    action: 'deactivate' | 'suspend';
    title: string;
    message: string;
  }>({ open: false, userId: 0, action: 'deactivate', title: '', message: '' });

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, mapping: OrgStaff) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuUser(mapping);
  };

  const handleRowClick = (mapping: OrgStaff) => {
    setDetailUserMapping(mapping);
    setDetailDrawerOpen(true);
  };

  const handleRoleClick = (mapping: OrgStaff) => {
    setSelectedMapping(mapping);
    setIsUpdateModalOpen(true);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuUser(null);
  };

  const handleStatusClick = (userId: number, action: 'activate' | 'deactivate' | 'suspend') => {
    handleMenuClose();
    if (action === 'activate') {
      setConfirmDialog({ open: true, userId });
    } else {
      const actionLabels: Record<string, string> = { deactivate: 'Deactivate', suspend: 'Suspend' };
      setActionPopup({
        open: true,
        userId,
        action,
        title: `${actionLabels[action]} Staff Member`,
        message: `Are you sure you want to ${action} this staff member?`,
      });
    }
  };

  const handleConfirmActivate = () => {
    dispatch(updateOrgStaffStatus({ user_id: confirmDialog.userId, action: 'activate' }));
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  const handleActionSubmit = (remark?: string) => {
    dispatch(updateOrgStaffStatus({
      user_id: actionPopup.userId,
      action: actionPopup.action,
      remark: remark || undefined,
    }));
    setActionPopup({ ...actionPopup, open: false });
  };

  useEffect(() => {
    setActivePage('staff-management');
    setPath([
      { title: 'Organization', path: '/dashboard' },
      { title: 'Staff Management', path: '/access-control/staff' },
    ]);
    setIsShow(true);
    dispatch(fetchOrgStaff());
    dispatch(fetchOrgRoles());
  }, [dispatch, setActivePage, setPath, setIsShow]);

  useEffect(() => {
    if (actionSuccess) {
      setAlert({ message: actionMessage || 'Operation successful', severity: 'success' });
      setIsModalOpen(false);
      setIsUpdateModalOpen(false);
      setSelectedMapping(null);
      dispatch(fetchOrgStaff());
      dispatch(resetAccessControlActionState());
    }
    if (actionError) {
      setAlert({ message: actionError, severity: 'error' });
      dispatch(resetAccessControlActionState());
    }
  }, [actionSuccess, actionError, actionMessage, dispatch]);

  useEffect(() => {
    if (detailUserMapping && Array.isArray(staff)) {
      const updatedMember = staff.find(m => m.mapping_id === detailUserMapping.mapping_id);
      if (updatedMember) {
        setDetailUserMapping(updatedMember);
      }
    }
  }, [staff, detailUserMapping]);

  return (
    <MainContainer>
      {alert && (
        <AlertPopup
          message={alert.message}
          severity={alert.severity}
          open={!!alert}
          onClose={() => setAlert(null)}
        />
      )}

      <HeaderContainer>
        <PageTitle>Staff Management</PageTitle>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsModalOpen(true)}
            sx={{
              textTransform: 'none',
              backgroundColor: '#5E8E7E',
              '&:hover': { backgroundColor: '#4A7A6A' }
            }}
          >
            Add Staff Member
          </Button>
        </Box>
      </HeaderContainer>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #eee', borderRadius: '8px' }}>
        <Table sx={{ minWidth: 650 }} aria-label="staff table">
          <StyledTableHead>
            <StyledTableRow>
              <StyledTableCell>ID</StyledTableCell>
              <StyledTableCell>Name</StyledTableCell>
              <StyledTableCell>Email</StyledTableCell>
              <StyledTableCell>Phone</StyledTableCell>
              <StyledTableCell>Role</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell align="right">Actions</StyledTableCell>
            </StyledTableRow>
          </StyledTableHead>
          <TableBody>
            {Array.isArray(staff) && staff.length > 0 ? (
              staff.map((member) => (
                <StyledTableRow 
                  key={member.mapping_id}
                  onClick={() => handleRowClick(member)}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#F9FAF9 !important' } }}
                >
                  <StyledTableCell>{member.mapping_id}</StyledTableCell>
                  <StyledTableCell>
                    {member.user.first_name} {member.user.last_name}
                  </StyledTableCell>
                  <StyledTableCell>{member.user.email}</StyledTableCell>
                  <StyledTableCell>{member.user.phone_number || 'N/A'}</StyledTableCell>
                  <StyledTableCell>
                    <Tooltip title="Click to update role">
                      <Chip 
                        label={member.system_role?.role_name || 'N/A'} 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRoleClick(member);
                        }}
                        sx={{ 
                          backgroundColor: '#E8F5E9', 
                          color: '#2E7D32', 
                          fontWeight: 500,
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#C8E6C9' }
                        }}
                      />
                    </Tooltip>
                  </StyledTableCell>
                  <StyledTableCell>
                    {(() => {
                      if (member.user.is_suspended) {
                        return (
                          <Chip 
                            label="Suspended" 
                            size="small" 
                            color="error"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        );
                      }
                      if (!member.user.is_active) {
                        return (
                          <Chip 
                            label="Deactivated" 
                            size="small" 
                            color="default"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        );
                      }
                      return (
                        <Chip 
                          label="Active" 
                          size="small" 
                          color="success"
                          variant="outlined"
                          sx={{ fontWeight: 500 }}
                        />
                      );
                    })()}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    <Tooltip title="Actions">
                      <IconButton 
                        onClick={(e) => handleMenuOpen(e, member)}
                        sx={{ color: '#666' }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                  </StyledTableCell>
                </StyledTableRow>
              ))
            ) : (
              <StyledTableRow>
                <StyledTableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  {isLoading ? <CircularProgress size={24} /> : 'No staff members found.'}
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 2,
          sx: { minWidth: 150, mt: 1 }
        }}
      >
        {menuUser && menuUser.user.is_active ? (
          <MenuItem onClick={() => handleStatusClick(menuUser.user.id, 'deactivate')}>
            Deactivate User
          </MenuItem>
        ) : (
          <MenuItem onClick={() => handleStatusClick(menuUser?.user.id as number, 'activate')}>
            Activate User
          </MenuItem>
        )}
        {menuUser && !menuUser.user.is_suspended && (
          <MenuItem onClick={() => handleStatusClick(menuUser.user.id, 'suspend')} sx={{ color: 'warning.main' }}>
            Suspend User
          </MenuItem>
        )}
      </Menu>

      <ConfirmationDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        onConfirm={handleConfirmActivate}
        title="Activate Staff Member"
        message="Are you sure you want to activate this staff member?"
      />

      <ActionPopup
        open={actionPopup.open}
        onClose={() => setActionPopup({ ...actionPopup, open: false })}
        onSubmit={handleActionSubmit}
        title={actionPopup.title}
        message={actionPopup.message}
        submitButtonText="Confirm"
        textFieldLabel="Reason"
        textFieldPlaceholder="Please provide a reason..."
        required
      />

      <StaffModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      <UpdateStaffRoleModal
        open={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        mapping={selectedMapping}
      />

      <StaffDetailDrawer
        open={detailDrawerOpen}
        onClose={() => setDetailDrawerOpen(false)}
        mapping={detailUserMapping}
      />
    </MainContainer>
  );
};

export default StaffManagement;
